import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Beaker, Save, RotateCcw, Zap, TrendingUp, TrendingDown, ArrowRight, Plus, Trash2, AlertTriangle as AlertTriangleIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NUM_FIELDS = [
  "demand_multiplier", "market_consultation_fee", "market_surgery_fee",
  "market_ward_rate", "market_private_rate", "market_icu_rate",
  "short_term_interest", "long_term_interest", "salary_cost_modifier", "supplies_cost_modifier",
];

const DEFAULTS = {
  demand_multiplier: 1.0,
  market_consultation_fee: 150,
  market_surgery_fee: 5000,
  market_ward_rate: 200,
  market_private_rate: 500,
  market_icu_rate: 1500,
  short_term_interest: 0.08,
  long_term_interest: 0.05,
  salary_cost_modifier: 1.0,
  supplies_cost_modifier: 1.0,
  scenario_name: "",
  scenario_description: "",
  yearly_modifiers: [],
  pending_events: [],
  events: [],
};

const EVENT_TYPES = [
  "health_inspection", "regional_epidemic", "staff_strike", "philanthropist_donation",
  "tech_breakthrough", "health_reform", "talent_drain", "population_growth",
];

const EVENT_SEVERITY_COLORS = {
  critical: "border-red-200 bg-red-50 text-red-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  info: "border-sky-200 bg-sky-50 text-sky-700",
};

const EVENT_SEVERITY_MAP = {
  health_inspection: "warning", regional_epidemic: "critical", staff_strike: "critical",
  philanthropist_donation: "info", tech_breakthrough: "info", health_reform: "warning",
  talent_drain: "warning", population_growth: "info",
};

// Each preset has a base config + yearly_modifiers for dynamic evolution
const PRESETS = [
  {
    id: "normal",
    icon: "balance",
    color: "bg-slate-100 text-slate-600 border-slate-200",
    config: { ...DEFAULTS, scenario_name: "preset_normal", yearly_modifiers: [] },
  },
  {
    id: "pandemic",
    icon: "virus",
    color: "bg-red-50 text-red-600 border-red-200",
    config: {
      ...DEFAULTS,
      scenario_name: "preset_pandemic",
      demand_multiplier: 1.0,
      // Base is normal; yearly_modifiers override per year
      yearly_modifiers: [
        { demand_multiplier: 1.3, supplies_cost_modifier: 1.4, salary_cost_modifier: 1.1, market_consultation_fee: 180 },
        { demand_multiplier: 1.8, supplies_cost_modifier: 2.2, salary_cost_modifier: 1.4, market_consultation_fee: 220, market_surgery_fee: 7000, market_icu_rate: 2500 },
        { demand_multiplier: 1.4, supplies_cost_modifier: 1.6, salary_cost_modifier: 1.25, market_consultation_fee: 190, market_surgery_fee: 5500 },
        { demand_multiplier: 0.85, supplies_cost_modifier: 1.1, salary_cost_modifier: 1.05, short_term_interest: 0.10, long_term_interest: 0.07 },
      ],
    },
  },
  {
    id: "recession",
    icon: "trending_down",
    color: "bg-amber-50 text-amber-600 border-amber-200",
    config: {
      ...DEFAULTS,
      scenario_name: "preset_recession",
      yearly_modifiers: [
        { demand_multiplier: 0.9, market_consultation_fee: 140, market_surgery_fee: 4500, short_term_interest: 0.10, long_term_interest: 0.07 },
        { demand_multiplier: 0.65, market_consultation_fee: 110, market_surgery_fee: 3500, market_ward_rate: 150, short_term_interest: 0.15, long_term_interest: 0.10, salary_cost_modifier: 0.85 },
        { demand_multiplier: 0.75, market_consultation_fee: 125, market_surgery_fee: 4000, short_term_interest: 0.12, long_term_interest: 0.08, salary_cost_modifier: 0.9 },
        { demand_multiplier: 0.9, market_consultation_fee: 140, market_surgery_fee: 4500, short_term_interest: 0.09, long_term_interest: 0.06 },
      ],
    },
  },
  {
    id: "boom",
    icon: "rocket",
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    config: {
      ...DEFAULTS,
      scenario_name: "preset_boom",
      yearly_modifiers: [
        { demand_multiplier: 1.2, market_consultation_fee: 160, salary_cost_modifier: 1.05 },
        { demand_multiplier: 1.5, market_consultation_fee: 180, market_surgery_fee: 6000, salary_cost_modifier: 1.15, supplies_cost_modifier: 1.1 },
        { demand_multiplier: 1.7, market_consultation_fee: 200, market_surgery_fee: 6500, market_private_rate: 700, salary_cost_modifier: 1.25, supplies_cost_modifier: 1.2 },
        { demand_multiplier: 1.4, market_consultation_fee: 175, market_surgery_fee: 5500, salary_cost_modifier: 1.1, short_term_interest: 0.06, long_term_interest: 0.04 },
      ],
    },
  },
  {
    id: "inflation",
    icon: "flame",
    color: "bg-orange-50 text-orange-600 border-orange-200",
    config: {
      ...DEFAULTS,
      scenario_name: "preset_inflation",
      yearly_modifiers: [
        { salary_cost_modifier: 1.15, supplies_cost_modifier: 1.3, market_consultation_fee: 170, market_surgery_fee: 5500, short_term_interest: 0.12, long_term_interest: 0.08 },
        { salary_cost_modifier: 1.4, supplies_cost_modifier: 1.7, market_consultation_fee: 200, market_surgery_fee: 6500, market_ward_rate: 280, short_term_interest: 0.18, long_term_interest: 0.12, demand_multiplier: 0.9 },
        { salary_cost_modifier: 1.6, supplies_cost_modifier: 2.0, market_consultation_fee: 230, market_surgery_fee: 7500, market_ward_rate: 320, market_icu_rate: 2200, short_term_interest: 0.22, long_term_interest: 0.15, demand_multiplier: 0.8 },
        { salary_cost_modifier: 1.3, supplies_cost_modifier: 1.5, market_consultation_fee: 190, market_surgery_fee: 6000, short_term_interest: 0.14, long_term_interest: 0.09, demand_multiplier: 0.95 },
      ],
    },
  },
];

// Resolve a year's effective values by merging base + yearly override
function resolveYear(config, yearIdx) {
  const base = {};
  NUM_FIELDS.forEach((k) => { base[k] = config[k] ?? DEFAULTS[k]; });
  const mods = config.yearly_modifiers || [];
  if (mods[yearIdx]) {
    Object.keys(mods[yearIdx]).forEach((k) => {
      if (NUM_FIELDS.includes(k)) base[k] = mods[yearIdx][k];
    });
  }
  return base;
}

// Key metrics to show in the timeline
const TIMELINE_KEYS = [
  { key: "demand_multiplier", fmt: (v) => `${(v * 100).toFixed(0)}%` },
  { key: "salary_cost_modifier", fmt: (v) => `${(v * 100).toFixed(0)}%` },
  { key: "supplies_cost_modifier", fmt: (v) => `${(v * 100).toFixed(0)}%` },
  { key: "short_term_interest", fmt: (v) => `${(v * 100).toFixed(1)}%` },
];

export default function ScenarioBuilder({ simId }) {
  const { t } = useTranslation();
  const [config, setConfig] = useState({ ...DEFAULTS });
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingYear, setEditingYear] = useState(null); // null = base, 0..N = year index
  const [totalYears, setTotalYears] = useState(4);

  useEffect(() => {
    loadScenario();
  }, [simId]);

  const loadScenario = async () => {
    try {
      const [scenRes, simRes] = await Promise.all([
        axios.get(`${API}/simulations/${simId}/scenario`),
        axios.get(`${API}/simulations/${simId}`),
      ]);
      setConfig({ ...DEFAULTS, ...scenRes.data });
      if (simRes.data?.rounds_count) setTotalYears(simRes.data.rounds_count);
    } catch {
      setConfig({ ...DEFAULTS });
    }
  };

  const update = (key, value) => {
    if (editingYear === null) {
      setConfig((prev) => ({ ...prev, [key]: value }));
    } else {
      setConfig((prev) => {
        const mods = [...(prev.yearly_modifiers || [])];
        while (mods.length <= editingYear) mods.push({});
        mods[editingYear] = { ...mods[editingYear], [key]: value };
        return { ...prev, yearly_modifiers: mods };
      });
    }
    setDirty(true);
  };

  const getEffective = (key) => {
    if (editingYear === null) return config[key] ?? DEFAULTS[key];
    const resolved = resolveYear(config, editingYear);
    return resolved[key];
  };

  const applyPreset = (preset) => {
    // Ensure yearly_modifiers array matches totalYears
    const mods = [...(preset.config.yearly_modifiers || [])];
    while (mods.length < totalYears) {
      mods.push(mods.length > 0 ? {} : {});
    }
    setConfig({ ...preset.config, yearly_modifiers: mods.slice(0, totalYears), scenario_description: config.scenario_description });
    setDirty(true);
    setEditingYear(null);
  };

  const save = async () => {
    setLoading(true);
    try {
      const saveConfig = { ...config };
      if (saveConfig.scenario_name.startsWith("preset_")) {
        const presetId = saveConfig.scenario_name.replace("preset_", "");
        saveConfig.scenario_name = t(`scenario.presets.${presetId}`);
      }
      await axios.put(`${API}/simulations/${simId}/scenario`, saveConfig);
      setConfig(saveConfig);
      toast.success(t("scenario.saved"));
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setConfig({ ...DEFAULTS });
    setEditingYear(null);
    setDirty(true);
  };

  const addEvent = () => {
    setConfig((prev) => ({
      ...prev,
      pending_events: [...(prev.pending_events || []), { event_type: EVENT_TYPES[Math.floor(Math.random() * EVENT_TYPES.length)], _uid: Date.now() + Math.random() }],
    }));
    setDirty(true);
  };

  const removeEvent = (idx) => {
    setConfig((prev) => ({
      ...prev,
      pending_events: (prev.pending_events || []).filter((_, i) => i !== idx),
    }));
    setDirty(true);
  };

  const updateEvent = (idx, field, value) => {
    setConfig((prev) => {
      const evts = [...(prev.pending_events || [])];
      evts[idx] = { ...evts[idx], [field]: value };
      return { ...prev, pending_events: evts };
    });
    setDirty(true);
  };

  const pctLabel = (v) => `${(v * 100).toFixed(0)}%`;
  const rateLabel = (v) => `${(v * 100).toFixed(1)}%`;
  const hasDynamic = (config.yearly_modifiers || []).some((m) => m && Object.keys(m).length > 0);
  const presetName = config.scenario_name?.startsWith("preset_")
    ? t(`scenario.presets.${config.scenario_name.replace("preset_", "")}`)
    : config.scenario_name;

  return (
    <div className="space-y-5" data-testid="scenario-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Beaker className="w-4 h-4 text-violet-500" />
          <h3 className="text-base font-heading font-semibold text-slate-900">{t("scenario.title")}</h3>
          {dirty && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600 bg-amber-50">{t("scenario.unsaved")}</Badge>}
          {hasDynamic && <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-600 bg-violet-50">{t("scenario.dynamic")}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reset} data-testid="scenario-reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />{t("scenario.reset")}
          </Button>
          <Button size="sm" onClick={save} disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="scenario-save-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" />{t("common.save")}
          </Button>
        </div>
      </div>

      {/* Presets */}
      <div className="grid grid-cols-5 gap-2" data-testid="scenario-presets">
        {PRESETS.map((p) => {
          const isActive = config.scenario_name === p.config.scenario_name ||
            presetName === t(`scenario.presets.${p.id}`);
          return (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-center transition-all duration-150 ${
                isActive
                  ? `${p.color} ring-2 ring-offset-1 ring-current shadow-sm`
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:shadow-sm"
              }`}
              data-testid={`preset-${p.id}`}
            >
              <span className="text-lg">
                {p.icon === "balance" && <Beaker className="w-5 h-5 mx-auto" />}
                {p.icon === "virus" && <Zap className="w-5 h-5 mx-auto" />}
                {p.icon === "trending_down" && <TrendingDown className="w-5 h-5 mx-auto" />}
                {p.icon === "rocket" && <TrendingUp className="w-5 h-5 mx-auto" />}
                {p.icon === "flame" && <Zap className="w-5 h-5 mx-auto -rotate-12" />}
              </span>
              <span className="text-[11px] font-medium leading-tight">{t(`scenario.presets.${p.id}`)}</span>
            </button>
          );
        })}
      </div>

      {/* Dynamic Timeline */}
      {hasDynamic && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-violet-500" />
              {t("scenario.timeline")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-stretch gap-1.5 overflow-x-auto pb-2">
              {Array.from({ length: totalYears }).map((_, i) => {
                const resolved = resolveYear(config, i);
                const isEditing = editingYear === i;
                const demandDelta = resolved.demand_multiplier - DEFAULTS.demand_multiplier;
                const costAvg = (resolved.salary_cost_modifier + resolved.supplies_cost_modifier) / 2;
                const costDelta = costAvg - 1.0;
                return (
                  <button
                    key={i}
                    onClick={() => setEditingYear(isEditing ? null : i)}
                    className={`flex-1 min-w-[120px] p-3 rounded-lg border text-left transition-all duration-150 ${
                      isEditing
                        ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`timeline-year-${i}`}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{t("common.round")} {i + 1}</p>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-500">{t("scenario.demandShort")}</span>
                        <span className={`text-xs font-mono font-medium ${demandDelta > 0 ? "text-emerald-600" : demandDelta < 0 ? "text-red-500" : "text-slate-600"}`}>
                          {demandDelta >= 0 ? "+" : ""}{(demandDelta * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-500">{t("scenario.costsShort")}</span>
                        <span className={`text-xs font-mono font-medium ${costDelta > 0.05 ? "text-red-500" : costDelta < -0.05 ? "text-emerald-600" : "text-slate-600"}`}>
                          {costDelta >= 0 ? "+" : ""}{(costDelta * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-500">{t("scenario.interestShort")}</span>
                        <span className="text-xs font-mono font-medium text-slate-600">
                          {(resolved.short_term_interest * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">{t("scenario.timelineHint")}</p>
          </CardContent>
        </Card>
      )}

      {/* Year Editing Indicator */}
      {editingYear !== null && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 border border-violet-200">
          <Badge className="bg-violet-600 text-white text-[10px]">{t("common.round")} {editingYear + 1}</Badge>
          <span className="text-xs text-violet-700">{t("scenario.editingYear")}</span>
          <button onClick={() => setEditingYear(null)} className="ml-auto text-xs text-violet-600 hover:underline" data-testid="editing-year-clear">
            {t("scenario.editBase")}
          </button>
        </div>
      )}

      {/* Name and Description */}
      {editingYear === null && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <Label className="text-xs">{t("scenario.scenarioName")}</Label>
              <Input
                value={presetName}
                onChange={(e) => update("scenario_name", e.target.value)}
                placeholder={t("scenario.namePlaceholder")}
                className="mt-1"
                data-testid="scenario-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">{t("scenario.description")}</Label>
              <Input
                value={config.scenario_description || ""}
                onChange={(e) => { setConfig(prev => ({ ...prev, scenario_description: e.target.value })); setDirty(true); }}
                placeholder={t("scenario.descPlaceholder")}
                className="mt-1"
                data-testid="scenario-desc-input"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parameter Editors */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("scenario.demandSection")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">{t("scenario.demandMultiplier")} ({pctLabel(getEffective("demand_multiplier"))})</Label>
            <Slider
              min={20} max={300} step={5}
              value={[getEffective("demand_multiplier") * 100]}
              onValueChange={([v]) => update("demand_multiplier", v / 100)}
              data-testid="scenario-demand-slider"
            />
            <p className="text-[10px] text-slate-400 mt-1">{t("scenario.demandHint")}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("scenario.marketPrices")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            {[
              ["market_consultation_fee", t("hospital.consultationFee"), 50, 500],
              ["market_surgery_fee", t("hospital.surgeryFee"), 1000, 20000],
              ["market_ward_rate", t("hospital.wardRate"), 50, 800],
              ["market_private_rate", t("hospital.privateRate"), 100, 2000],
              ["market_icu_rate", t("hospital.icuRate"), 300, 5000],
            ].map(([key, label, min, max]) => (
              <div key={key}>
                <Label className="text-xs">{label} (${getEffective(key)})</Label>
                <Input
                  type="number" min={min} max={max}
                  value={getEffective(key)}
                  onChange={(e) => update(key, parseFloat(e.target.value) || min)}
                  className="mt-1"
                  data-testid={`scenario-${key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("scenario.interestRates")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">{t("scenario.shortTermInterest")} ({rateLabel(getEffective("short_term_interest"))})</Label>
            <Slider
              min={0} max={30} step={0.5}
              value={[getEffective("short_term_interest") * 100]}
              onValueChange={([v]) => update("short_term_interest", v / 100)}
            />
          </div>
          <div>
            <Label className="text-xs">{t("scenario.longTermInterest")} ({rateLabel(getEffective("long_term_interest"))})</Label>
            <Slider
              min={0} max={20} step={0.5}
              value={[getEffective("long_term_interest") * 100]}
              onValueChange={([v]) => update("long_term_interest", v / 100)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("scenario.costModifiers")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">{t("scenario.salaryModifier")} ({pctLabel(getEffective("salary_cost_modifier"))})</Label>
            <Slider
              min={50} max={300} step={5}
              value={[getEffective("salary_cost_modifier") * 100]}
              onValueChange={([v]) => update("salary_cost_modifier", v / 100)}
            />
          </div>
          <div>
            <Label className="text-xs">{t("scenario.suppliesModifier")} ({pctLabel(getEffective("supplies_cost_modifier"))})</Label>
            <Slider
              min={50} max={300} step={5}
              value={[getEffective("supplies_cost_modifier") * 100]}
              onValueChange={([v]) => update("supplies_cost_modifier", v / 100)}
            />
          </div>
          <p className="text-[10px] text-slate-400">{t("scenario.costHint")}</p>
        </CardContent>
      </Card>

      {/* Events Section */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangleIcon className="w-4 h-4 text-amber-500" />
              {t("events.title")}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={addEvent} data-testid="add-event-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />{t("events.addEvent")}
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">{t("events.pendingHint") || "These events will be applied when you advance to the next year."}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {(!config.pending_events || config.pending_events.length === 0) && (
            <p className="text-xs text-slate-400 text-center py-3">{t("events.noEvents")}</p>
          )}
          {(config.pending_events || []).map((ev, idx) => {
            const sev = EVENT_SEVERITY_MAP[ev.event_type] || "info";
            return (
              <div
                key={ev._uid || idx}
                className={`flex items-center gap-3 rounded-lg border p-3 ${EVENT_SEVERITY_COLORS[sev]}`}
                data-testid={`event-row-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <Select value={ev.event_type} onValueChange={(v) => updateEvent(idx, "event_type", v)}>
                    <SelectTrigger className="h-8 text-xs bg-white" data-testid={`event-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((et) => (
                        <SelectItem key={et} value={et}>{t(`events.${et}.short`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  onClick={() => removeEvent(idx)}
                  className="p-1.5 rounded hover:bg-black/5 transition-colors"
                  data-testid={`event-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
